import {
  ALL_TIME_RANGES,
  calculateMacroPercentages,
  Calories,
  createCalories,
  createEmptyTimeBasedCalories,
  createGrams,
  createMilligrams,
  DailyNutrition,
  DailyNutritionSummary,
  MacroChartData,
  NutritionalValue,
  NutritionGoals,
  TimeBasedCalories,
  TimeBasedChartData,
  TimeRange,
  WeeklyNutritionSummary,
} from '@/types/nutrition'
import {
  endOfWeek,
  formatWeekRange as formatWeekRangeUtil,
  getDayName,
  getMillisecondsDifference,
  getWeekDays,
  isSameDay,
  startOfWeek,
} from './date'

/**
 * Gets the start of the week (Monday) for a given date.
 * Re-exported from centralized date utilities for convenience.
 */
export const getWeekStart = startOfWeek

/**
 * Gets the end of the week (Sunday) for a given date.
 * Re-exported from centralized date utilities for convenience.
 */
export const getWeekEnd = endOfWeek

/**
 * Formats a week range for display.
 * Re-exported from centralized date utilities for convenience.
 */
export const formatWeekRange = formatWeekRangeUtil

/**
 * Generates all days in a week with empty nutrition data.
 * Re-exported from centralized date utilities for convenience.
 */
export const generateWeekDays = getWeekDays

/**
 * Creates empty nutritional values.
 * @returns Empty NutritionalValue with all values set to 0
 */
export const createEmptyNutritionalValue = (): NutritionalValue => ({
  calories: createCalories(0),
  carbs: createGrams(0),
  fat: createGrams(0),
  protein: createGrams(0),
  cholesterol: createMilligrams(0),
  sodium: createMilligrams(0),
  sugar: createGrams(0),
  fiber: createGrams(0),
})

/**
 * Adds two nutritional values together.
 * @param a - First nutritional value
 * @param b - Second nutritional value
 * @returns Sum of both nutritional values
 */
export const addNutritionalValues = (
  a: NutritionalValue,
  b: NutritionalValue
): NutritionalValue => ({
  calories: createCalories(a.calories + b.calories),
  carbs: createGrams(a.carbs + b.carbs),
  fat: createGrams(a.fat + b.fat),
  protein: createGrams(a.protein + b.protein),
  cholesterol: createMilligrams(a.cholesterol + b.cholesterol),
  sodium: createMilligrams(a.sodium + b.sodium),
  sugar: createGrams(a.sugar + b.sugar),
  fiber: createGrams(a.fiber + b.fiber),
})

/**
 * Calculates average of nutritional values over multiple days.
 * @param values - Array of nutritional values
 * @param divisor - Number to divide by (e.g., number of days)
 * @returns Average nutritional values
 */
export const calculateNutritionalAverage = (
  values: NutritionalValue[],
  divisor: number
): NutritionalValue => {
  if (divisor === 0) return createEmptyNutritionalValue()

  const total = values.reduce(addNutritionalValues, createEmptyNutritionalValue())

  return {
    calories: createCalories(Math.round(total.calories / divisor)),
    carbs: createGrams(Math.round((total.carbs / divisor) * 10) / 10),
    fat: createGrams(Math.round((total.fat / divisor) * 10) / 10),
    protein: createGrams(Math.round((total.protein / divisor) * 10) / 10),
    cholesterol: createMilligrams(Math.round(total.cholesterol / divisor)),
    sodium: createMilligrams(Math.round(total.sodium / divisor)),
    sugar: createGrams(Math.round((total.sugar / divisor) * 10) / 10),
    fiber: createGrams(Math.round((total.fiber / divisor) * 10) / 10),
  }
}

/**
 * Checks if a string is a valid TimeRange format (e.g., '00-04', '12-16')
 */
const isValidTimeRange = (category: string): category is TimeRange => {
  return ALL_TIME_RANGES.includes(category as TimeRange)
}

/**
 * Gets the time range for a meal category, handling both meal names and direct time ranges.
 *
 * This function supports backward compatibility by accepting both:
 * - Traditional meal names: 'Breakfast', 'Lunch', 'Dinner', 'Late meal'
 * - Direct time ranges: '00-04', '04-08', '08-12', '12-16', '16-20', '20-24'
 *
 * @param mealCategory - Either a traditional meal name or a direct time range string
 * @returns The corresponding TimeRange for the meal
 *
 * @example
 * ```typescript
 * // Traditional meal name
 * getTimeRangeForMeal('Breakfast') // Returns '08-12'
 *
 * // Direct time range (already in correct format)
 * getTimeRangeForMeal('16-20') // Returns '16-20'
 *
 * // Unknown meal defaults to lunch time
 * getTimeRangeForMeal('Snack') // Returns '12-16'
 * ```
 */
const getTimeRangeForMeal = (mealCategory: string): TimeRange => {
  // Check if the meal category is already a time range (new format)
  if (isValidTimeRange(mealCategory)) {
    return mealCategory
  }

  // Otherwise use traditional meal name mapping (old format)
  const mealTimeMapping: Record<string, TimeRange> = {
    Breakfast: '08-12',
    Lunch: '12-16',
    Dinner: '16-20',
    'Late meal': '20-24',
  }

  return mealTimeMapping[mealCategory] ?? '12-16' // Default to lunch time for unknown categories
}

/**
 * Creates time-based calorie distribution from meal data.
 * Supports both traditional meal names (Breakfast, Lunch, Dinner, Late meal)
 * and direct time range categories (00-04, 12-16, 16-20, 20-24).
 *
 * @param dailyNutrition - Daily nutrition data
 * @returns Time-based calorie distribution
 */
export const createTimeBasedCalories = (
  dailyNutrition: DailyNutrition
): Record<TimeRange, Calories> => {
  const timeBasedCalories = createEmptyTimeBasedCalories()

  dailyNutrition.meals.forEach((meal) => {
    const timeRange = getTimeRangeForMeal(meal.category)
    const mealCalories = meal.foods.reduce((sum, food) => sum + food.nutrition.calories, 0)
    timeBasedCalories[timeRange] = createCalories(timeBasedCalories[timeRange] + mealCalories)
  })

  return timeBasedCalories
}

/**
 * Creates a daily nutrition summary for chart display.
 * @param date - Date for the summary
 * @param dailyNutrition - Daily nutrition data (optional)
 * @returns Daily nutrition summary
 */
export const createDailyNutritionSummary = (
  date: Date,
  dailyNutrition?: DailyNutrition
): DailyNutritionSummary => {
  const dayOfWeek = getDayName(date)

  if (!dailyNutrition) {
    return {
      date,
      dayOfWeek,
      nutrition: createEmptyNutritionalValue(),
      macroPercentages: { carbsPercent: 0, fatPercent: 0, proteinPercent: 0 },
      timeBasedCalories: createEmptyTimeBasedCalories(),
    }
  }

  const macroPercentages = calculateMacroPercentages(dailyNutrition.totals)
  const timeBasedCalories = createTimeBasedCalories(dailyNutrition)

  return {
    date,
    dayOfWeek,
    nutrition: dailyNutrition.totals,
    macroPercentages,
    timeBasedCalories,
  }
}

/**
 * Creates a weekly nutrition summary from daily data.
 * @param weekStart - Start date of the week
 * @param dailyData - Array of daily nutrition data
 * @returns Weekly nutrition summary
 */
export const createWeeklyNutritionSummary = (
  weekStart: Date,
  dailyData: readonly DailyNutrition[]
): WeeklyNutritionSummary => {
  const weekEnd = getWeekEnd(weekStart)
  const weekDays = generateWeekDays(weekStart)

  // Create daily summaries for all days of the week
  const dailySummaries: DailyNutritionSummary[] = weekDays.map((day) => {
    const dailyNutrition = dailyData.find((entry) => isSameDay(entry.date, day))
    return createDailyNutritionSummary(day, dailyNutrition)
  })

  // Calculate weekly averages and totals
  const nutritionalValues = dailySummaries.map((summary) => summary.nutrition)
  const weeklyTotals = nutritionalValues.reduce(addNutritionalValues, createEmptyNutritionalValue())
  const weeklyAverages = calculateNutritionalAverage(nutritionalValues, 7)

  // Create time-based calories for the week
  const timeBasedCalories: TimeBasedCalories[] = dailySummaries.map((summary) => ({
    date: summary.date,
    timeRanges: summary.timeBasedCalories,
    totalCalories: summary.nutrition.calories,
  }))

  return {
    weekStart,
    weekEnd,
    dailySummaries,
    weeklyAverages,
    weeklyTotals,
    timeBasedCalories,
  }
}

/**
 * Converts weekly data to macro chart data for Recharts.
 * @param weeklyData - Weekly nutrition summary
 * @returns Array of macro chart data points
 */
export const convertToMacroChartData = (weeklyData: WeeklyNutritionSummary): MacroChartData[] => {
  return weeklyData.dailySummaries.map((summary) => {
    const { nutrition, dayOfWeek } = summary

    // Convert macros to calories for chart display
    const carbsCalories = nutrition.carbs * 4
    const fatCalories = nutrition.fat * 9
    const proteinCalories = nutrition.protein * 4

    return {
      day: dayOfWeek,
      carbs: nutrition.carbs,
      fat: nutrition.fat,
      protein: nutrition.protein,
      carbsCalories,
      fatCalories,
      proteinCalories,
    }
  })
}

/**
 * Converts weekly data to time-based chart data for Recharts.
 * @param weeklyData - Weekly nutrition summary
 * @returns Array of time-based chart data points
 */
export const convertToTimeBasedChartData = (
  weeklyData: WeeklyNutritionSummary
): TimeBasedChartData[] => {
  return weeklyData.dailySummaries.map((summary) => ({
    day: summary.dayOfWeek,
    '00-04': summary.timeBasedCalories['00-04'],
    '04-08': summary.timeBasedCalories['04-08'],
    '08-12': summary.timeBasedCalories['08-12'],
    '12-16': summary.timeBasedCalories['12-16'],
    '16-20': summary.timeBasedCalories['16-20'],
    '20-24': summary.timeBasedCalories['20-24'],
    total: summary.nutrition.calories,
  }))
}

/**
 * Calculates average daily calorie intake vs. goals.
 * @param weeklyData - Weekly nutrition summary
 * @param goals - Nutrition goals for comparison
 * @returns Object with averages and goal comparisons
 */
export const calculateWeeklyGoalComparison = (
  weeklyData: WeeklyNutritionSummary,
  goals: NutritionGoals
) => {
  const avgDailyCalories = weeklyData.weeklyAverages.calories
  const avgMacroPercentages = calculateMacroPercentages(weeklyData.weeklyAverages)
  const totalWeeklyCalories = weeklyData.weeklyTotals.calories
  const goalWeeklyCalories = goals.dailyCalories * 7

  return {
    averages: {
      dailyCalories: avgDailyCalories,
      macroPercentages: avgMacroPercentages,
    },
    goals: {
      dailyCalories: goals.dailyCalories,
      macroTargets: goals.macroTargets,
    },
    comparisons: {
      caloriesDifference: avgDailyCalories - goals.dailyCalories,
      weeklyCaloriesDifference: totalWeeklyCalories - goalWeeklyCalories,
      macroDeviations: {
        carbs:
          Math.round((avgMacroPercentages.carbsPercent - goals.macroTargets.carbsPercent) * 10) /
          10,
        fat: Math.round((avgMacroPercentages.fatPercent - goals.macroTargets.fatPercent) * 10) / 10,
        protein:
          Math.round(
            (avgMacroPercentages.proteinPercent - goals.macroTargets.proteinPercent) * 10
          ) / 10,
      },
    },
  }
}

/**
 * Filters daily nutrition data for a specific week.
 * @param data - Array of daily nutrition data
 * @param weekStart - Start date of the week to filter
 * @param weekEnd - Optional end date of the week. If not provided, defaults to Sunday of the week (6 days after start)
 * @returns Array of daily nutrition data for the specified week
 */
export const filterDataForWeek = (
  data: readonly DailyNutrition[],
  weekStart: Date,
  weekEnd?: Date
): readonly DailyNutrition[] => {
  const endDate = weekEnd ?? getWeekEnd(weekStart)

  return data.filter((entry) => {
    if (entry.date === null || entry.date === undefined) return false
    const startTime = getMillisecondsDifference(weekStart, entry.date)
    const endTime = getMillisecondsDifference(entry.date, endDate)
    return startTime >= 0 && endTime >= 0
  })
}
